import React from 'react'
import { NodeViewWrapper } from '@tiptap/react'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Editor } from '@tiptap/core'
import { ExternalLink } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import type { QuotedSourcesAttributes } from './quoted-sources-extension'
import { SourceTypeIcon } from '@/utils/file-icons'

interface QuotedSourcesNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function QuotedSourcesNodeView({
  node,
  editor,
  selected,
}: QuotedSourcesNodeViewProps) {
  const attrs = node.attrs as QuotedSourcesAttributes
  const { sources = [] } = attrs
  const navigate = useNavigate()


  const handleViewSource = (sourceId: string, sourceType: string) => {
    const routes: Record<string, string> = {
      flow: `/flows/${sourceId}`,
      internal_doc: `/topics/${sourceId}`,
      citation: `/citations/${sourceId}`,
      glossary: `/glossary/${sourceId}`,
    }
    const route = routes[sourceType]
    if (route) {
      navigate({ to: route as any })
    }
  }

  if (sources.length === 0) {
    return null
  }

  return (
    <NodeViewWrapper
      className={cn(
        'quoted-sources-wrapper',
        'my-8 border-t-2 border-gray-200 pt-6 dark:border-gray-700',
        selected && 'ring-2 ring-blue-500'
      )}
    >
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Quoted Sources
        </h3>

        <div className="space-y-3">
          {sources.map((source) => (
            <Card
              key={`${source.sourceType}-${source.sourceId}`}
              className="flex items-start gap-3 p-4"
            >
              <Badge
                variant="outline"
                className="mt-0.5 h-6 w-6 flex-shrink-0 items-center justify-center rounded-full p-0 font-mono text-xs"
              >
                {source.number}
              </Badge>

              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <SourceTypeIcon
                    fileName={source.sourceTitle || ''}
                    type={source.sourceType}
                  />
                  <h4 className="font-medium text-sm">{source.sourceTitle}</h4>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {source.sourceType.replace('_', ' ')}
                  </Badge>
                </div>

                {source.snippet && (
                  <blockquote className="text-muted-foreground border-l-2 border-blue-300 pl-3 text-sm italic dark:border-blue-700">
                    "{source.snippet}"
                  </blockquote>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-xs">
                    Cited {source.usageCount}x in this document
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => handleViewSource(source.sourceId, source.sourceType)}
                  >
                    <ExternalLink className="mr-1.5 h-3 w-3" />
                    View Source
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <p className="text-muted-foreground text-xs italic">
          These sources were cited throughout this document to provide context and attribution.
        </p>
      </div>
    </NodeViewWrapper>
  )
}
